import React, { useContext, useState } from "react";
import { connect } from "react-redux";
import { TableContext, PopupContext } from "../../index";
import { showDangerToast, showSuccessToast, showWarningToast } from "../../validation/ToastContainer";
import { POPUP_TYPES, getConfirmPopup, getDeletePopup } from "../../view/popup/PopupContainer";
import { Checkbox } from "../../view/Checkbox";
import { deepCopy } from "../../utils";
import { useHistory } from "react-router-dom";
import { MoreVertical, Pencil, Eye, Copy, MoveRight, Trash2 } from 'lucide-react';
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import * as ruleService from "../ruleService";
import { useMsbTableHelper } from "./useMsbTableHelper";

const Severity = {
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low'
}

function MsbTableRow({ configPath, data, index, hiddenHeaders = [] }) {

    const history = useHistory();
    const { config, setConfig } = useContext(TableContext);

    const [anchorEl, setAnchorEl] = React.useState(null);
    //const [msbSelected, setMsbSelected] = useState(false);

    const { setPopup } = useContext(PopupContext);
    const helper = useMsbTableHelper();
    const [msbId] = useState();

    const menuOpen = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const getCircleType = () => {
        switch (data.severity.toLowerCase()) {
            case Severity.HIGH:
                return "severity-high";
            case Severity.MEDIUM:
                return "severity-medium";
            case Severity.LOW:
                return "severity-low";
            default:
                return null;
        }
    }

    const onCheckboxChange = () => {
        let selectedRows = deepCopy(config.selected);
        let rules = deepCopy(config.rules);
        if (selectedRows.includes(data.id)) {
            selectedRows.splice(selectedRows.indexOf(data.id), 1);

            // Find the index of the rule with the matching id
            const index = rules.findIndex(row => row.id === data.id);
            // If the rule is found (i.e., index is not -1), remove it
            if (index !== -1) {
                rules.splice(index, 1); // Remove the object at the found index
            }

        } else {
            selectedRows.push(data.id);
            rules.push(data);
        }
        setConfig({ ...config, selected: selectedRows, rules: rules })
    }

    const convertDate = (dateString) => {
        if (!dateString) {
            return ''
        }
        const shortDate = dateString.split('T')[0];
        return shortDate.split('-').reverse().join('/');
    }

    // useEffect(() => {
    //     const filters = urlService.getFilterQueryString();
    //     if (filters.includes('f_msbId')) {
    //         setMsbSelected(true);  //true to show the 3 dots on end of each row
    //     }
    //     else {
    //         setMsbSelected(false);
    //     }
    // }, [data])



    const onViewEditRule = () => {
        handleClose();
        history.push(`/add-rules/${data.id}`);
    }

    const onCopyRule = () => {
        handleClose();
        setPopup(
            {
                type: POPUP_TYPES.COPY_TO_MSB,
                title: "Copy to Selected Msb",
                msbId: msbId,
                confirm: confirmCopyRule
            }
        );
    }

    const confirmCopyRule = async (msbId) => {
        const ruleId = data?.id;

        if (!ruleId) {
            showWarningToast('Something went wrong. Copy rule did not happen!', 'Copy Rule', 'aborted');
            return;
        }

        CopyRule(ruleId, msbId);
    };

    const CopyRule = (rule, selectedMsbId) => {
        const rules = [];
        rules.push(rule);
        const onConfirm = async () => {
            if (!rules || rules.length === 0) {
                return showWarningToast('Something went wrong. Copy rule did not happened!', 'Copy Rule', 'aborted');
            }
            ruleService.copyRuleList(rules, selectedMsbId)
                .then((success) => {
                    if (success) {
                        helper?.hardReload?.();
                        showSuccessToast('Rule is copied', 'Copy Rule', 'success');
                    } else {
                        showDangerToast('Something went wrong. Copy rule did not happened!', 'Copy Rule');
                    }
                });
        }
        setPopup(
            getConfirmPopup('Are you sure?',
                'Copy Rule',
                () => onConfirm()
            )
        );

    }

    const onMoveRule = () => {
        handleClose();
        setPopup(
            {
                type: POPUP_TYPES.COPY_TO_MSB,
                title: "Move to Selected Msb",
                msbId: msbId,
                confirm: (selectedMsbId) => confirmMoveRule(selectedMsbId)
            }
        );
    }

    const confirmMoveRule = async (msbId) => {
        const ruleId = data?.id;

        if (!ruleId) {
            showWarningToast('Something went wrong. Move rule did not happen!', 'Move Rule', 'aborted');
            return;
        }

        MoveRule(ruleId, msbId);
    };

    const MoveRule = (rule, selectedMsbId) => {
        const rules = [];
        rules.push(rule);
        const onConfirm = async () => {
            if (!rules || rules.length === 0) {
                return showWarningToast('Something went wrong. Move rule did not happened', 'Move Rule', 'aborted');
            }
            ruleService.moveRuleList(rules, selectedMsbId)
                .then((success) => {
                    if (success) {
                        helper?.hardReload?.();
                        showSuccessToast('Rule is moved', 'Move Rule', 'success');
                    } else {
                        showDangerToast('Something went wrong', 'Move Rule');
                    }
                });
        }
        setPopup(
            getConfirmPopup('Are you sure?',
                'Move Rule',
                () => onConfirm()
            )
        );

    };

    const onRemoveRule = () => {
        handleClose();
        const rules = [];
        rules.push(data.id);
        const onConfirm = async () => {
            if (!rules || rules.length === 0) {
                return showWarningToast('Something went wrong. Remove rule did not happened!', 'Remove Rule', 'aborted');
            }
            ruleService.removeList(rules)
                .then((success) => {
                    if (success) {
                        helper.hardReload();
                        showSuccessToast('Rule is removed', 'Remove Rule', 'success');
                    } else {
                        showDangerToast('Something went wrong. Remove rule did not happened!', 'Remove Rule');
                    }
                });
        }
        setPopup(
            getDeletePopup('Are you sure you want to remove this rule?',
                'Remove Rule',
                () => onConfirm(),
                undefined,
                'Remove Rule'
            )
        );
    }


    return (
        <tr key={index}>
            <td>
                <Checkbox checked={config.selected.includes(data.id)} onClick={() => onCheckboxChange()} />
            </td>
            <td><strong>{data.name}</strong></td>
            {hiddenHeaders.includes("SECTION") ? null : <td>{data.sectionName || '-'}</td>}
            {hiddenHeaders.includes("SUBSECTION") ? null : <td>{data.subsectionName || '-'}</td>}
            <td>{data.baseRule ? 'FALSE' : 'TRUE'}</td>
            <td>{convertDate(data.createdOn)}</td>
            <td>{convertDate(data.updatedOn)}</td>
            <td>{data.user}</td>
            <td>
                <span className={`oct_severity-badge-v2 ${getCircleType()}`}>
                    {data.severity?.toUpperCase()}
                </span>
            </td>
            {/* {msbSelected &&  */}
            <td>
                <button className="oct_dropdown-trigger-v2" onClick={handleClick}>
                    <MoreVertical className="oct_icon" />
                </button>

                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleClose}
                    className="oct_menu-v2"
                >
                    {data.baseRule === true ?
                        <MenuItem onClick={() => onViewEditRule()} className="oct_menu-item-v2">
                            {/* data-toggle="tooltip" title="base rule" */}
                            <Eye size={16} className="me-2" />
                            View
                        </MenuItem>
                        :
                        <MenuItem onClick={() => onViewEditRule()} className="oct_menu-item-v2">
                            <Pencil size={16} className="me-2" />
                            Edit
                        </MenuItem>
                    }

                    <MenuItem onClick={() => onCopyRule()} className="oct_menu-item-v2">
                        <Copy size={16} className="me-2" />
                        Copy
                    </MenuItem>
                    <MenuItem
                        onClick={() => onMoveRule()}
                        disabled={data.baseRule === true}
                        className="oct_menu-item-v2">
                        <MoveRight size={16} className="me-2" />
                        Move
                    </MenuItem>
                    <MenuItem
                        onClick={() => onRemoveRule()}
                        disabled={data.baseRule === true}
                        className="oct_menu-item-v2">
                        <Trash2 size={16} className="me-2" />
                        Remove
                    </MenuItem>

                </Menu>

            </td>
            {/* } */}
        </tr>
    )

}

export default connect()(MsbTableRow);
